import { useCallback, useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { getSupabaseClient } from "../providers/supabase/supabase";

/**
 * Settings → Privacy - the CAN-SPAM / data-request center (decision #39).
 * One-click unsubscribes land here automatically (already honored - the
 * suppression is instant); deletion and export requests arrive as NEW and
 * wait for a human decision. "Remove contact data" runs the server-side
 * erase_contact function: events, notes, tasks, enrollments, and the
 * contact itself - the one sanctioned delete on the append-only spine.
 */

interface PrivacyRequestRow {
  id: string;
  kind: "unsubscribe" | "data_deletion" | "data_export";
  email: string;
  contact_id: number | null;
  detail: string | null;
  status: "new" | "completed" | "dismissed";
  requested_at: string;
  resolved_at: string | null;
  note: string | null;
}

const KIND_LABELS: Record<PrivacyRequestRow["kind"], string> = {
  unsubscribe: "Unsubscribe",
  data_deletion: "Delete my data",
  data_export: "Export my data",
};

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

export const PrivacyPage = () => {
  const [rows, setRows] = useState<PrivacyRequestRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirmErase, setConfirmErase] = useState<string | null>(null);

  const load = useCallback(async () => {
    const { data, error: err } = await getSupabaseClient()
      .from("privacy_requests")
      .select("*")
      .order("requested_at", { ascending: false })
      .limit(200);
    if (err) setError(err.message);
    else setRows((data ?? []) as PrivacyRequestRow[]);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const resolve = async (
    row: PrivacyRequestRow,
    status: "completed" | "dismissed",
    note?: string,
  ) => {
    const { error: err } = await getSupabaseClient()
      .from("privacy_requests")
      .update({ status, resolved_at: new Date().toISOString(), note: note ?? null })
      .eq("id", row.id);
    if (err) setError(err.message);
    await load();
  };

  const eraseAndComplete = async (row: PrivacyRequestRow) => {
    setBusyId(row.id);
    setError(null);
    try {
      const client = getSupabaseClient();
      // Match by the request's linked contact when we have one; otherwise
      // find contacts carrying this address.
      let contactIds: number[] = [];
      if (row.contact_id != null) {
        contactIds = [row.contact_id];
      } else {
        const { data, error: err } = await client
          .from("contacts")
          .select("id")
          .contains("email_jsonb", JSON.stringify([{ email: row.email }]));
        if (err) throw new Error(err.message);
        contactIds = (data ?? []).map((c: { id: number }) => c.id);
      }
      for (const id of contactIds) {
        const { error: err } = await client.rpc("erase_contact", { target: id });
        if (err) throw new Error(err.message);
      }
      await resolve(
        row,
        "completed",
        contactIds.length > 0
          ? `Erased ${contactIds.length} contact record${contactIds.length === 1 ? "" : "s"} and all related data.`
          : "No matching contact found - nothing to erase.",
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
      setConfirmErase(null);
    }
  };

  const open = (rows ?? []).filter((r) => r.status === "new");
  const closed = (rows ?? []).filter((r) => r.status !== "new");

  const statusChip = (r: PrivacyRequestRow) => (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${
        r.status === "new"
          ? "bg-[#c9a75a]/15 text-[#a3843c]"
          : r.status === "completed"
            ? "bg-[#6a8f6b]/15 text-[#6a8f6b]"
            : "bg-muted text-muted-foreground"
      }`}
    >
      {r.status === "new" ? "Needs action" : r.status === "completed" ? "Completed" : "Dismissed"}
    </span>
  );

  const requestRow = (r: PrivacyRequestRow) => (
    <div key={r.id} className="flex flex-col gap-2 py-3 border-t border-border first:border-t-0">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium">{KIND_LABELS[r.kind]}</span>
        {statusChip(r)}
        <span className="text-xs text-muted-foreground ml-auto">{fmtDate(r.requested_at)}</span>
      </div>
      <p className="text-sm text-muted-foreground">
        {r.email}
        {r.detail ? ` - "${r.detail}"` : ""}
        {r.note ? ` - ${r.note}` : ""}
      </p>
      {r.status === "new" && (
        <div className="flex flex-wrap items-center gap-2">
          {r.kind === "data_deletion" &&
            (confirmErase === r.id ? (
              <>
                <Button
                  variant="destructive"
                  size="sm"
                  disabled={busyId === r.id}
                  onClick={() => void eraseAndComplete(r)}
                >
                  {busyId === r.id ? "Erasing…" : "Yes, erase everything for this address"}
                </Button>
                <Button variant="outline" size="sm" onClick={() => setConfirmErase(null)}>
                  Cancel
                </Button>
              </>
            ) : (
              <Button
                variant="outline"
                size="sm"
                title="Deletes the contact and every note, task, enrollment, and timeline event - unrecoverable"
                onClick={() => setConfirmErase(r.id)}
              >
                Remove contact data
              </Button>
            ))}
          {r.kind === "data_export" && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => void resolve(r, "completed", "Export sent manually.")}
            >
              Mark completed
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void resolve(r, "dismissed")}
          >
            Dismiss
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <div className="flex flex-col gap-4">
      {error && (
        <p role="alert" className="text-sm text-[#c25d5d]">
          {error}
        </p>
      )}
      <Card>
        <CardContent className="p-4">
          <h3 className="text-sm font-semibold mb-1">Open requests</h3>
          <p className="text-xs text-muted-foreground mb-2">
            Deletion and export requests wait here for a decision. Unsubscribes
            never appear as open - the link honors them instantly.
          </p>
          {rows === null ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : open.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nothing needs action.</p>
          ) : (
            open.map(requestRow)
          )}
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-4">
          <h3 className="text-sm font-semibold mb-2">History</h3>
          {rows === null ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : closed.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No unsubscribes or resolved requests yet.
            </p>
          ) : (
            closed.map(requestRow)
          )}
        </CardContent>
      </Card>
      <p className="text-xs text-muted-foreground max-w-prose">
        Every marketing email sent by this workspace carries a one-click
        unsubscribe link and your mailing address (set it under Account
        defaults) - both required by CAN-SPAM. Opted-out contacts are
        suppressed from all automated sends automatically.
      </p>
    </div>
  );
};
